import { Key, useMemo } from 'react'
import type { NfdRecord } from 'api/api-client'
import { useWallet } from '@txnlab/use-wallet-react'
import { meetsMinimumVersion } from 'helpers/versions'

interface DNSCardProps {
  nfd: NfdRecord
}

// Helper function to get record type color
const getRecordTypeColor = (recordType: string) => {
  const typeColors: Record<string, string> = {
    a: 'text-blue-600 dark:text-blue-400',
    aaaa: 'text-purple-600 dark:text-purple-400',
    cname: 'text-green-600 dark:text-green-400',
    mx: 'text-orange-600 dark:text-orange-400',
    txt: 'text-pink-600 dark:text-pink-400',
    ns: 'text-yellow-600 dark:text-yellow-400',
    srv: 'text-indigo-600 dark:text-indigo-400',
    caa: 'text-red-600 dark:text-red-400'
  }

  return typeColors[recordType] || 'text-gray-600 dark:text-gray-400'
}

export default function DNSCard({ nfd }: DNSCardProps) {
  const dnsValue = useMemo(() => nfd.properties?.userDefined?.dns, [nfd])
  const { activeAddress } = useWallet()

  if (!dnsValue) {
    return null
  }

  try {
    // Try to parse the value as JSON
    const jsonValue = JSON.parse(dnsValue)

    // Function to render DNS records in a zone-file-like format
    const renderDnsRecords = () => {
      // If jsonValue is not an object or is empty, return a message
      if (
        typeof jsonValue !== 'object' ||
        jsonValue === null ||
        Object.keys(jsonValue).length === 0
      ) {
        return <div className="text-gray-500 dark:text-gray-400">No DNS records found</div>
      }

      return (
        <div className="font-mono text-sm">
          <div className="grid grid-cols-8 gap-2 mb-2 font-semibold text-gray-700 dark:text-gray-300">
            <div className="col-span-1">Type</div>
            <div className="col-span-3">Name</div>
            <div className="col-span-4">Value</div>
          </div>
          {Object.entries(jsonValue).map(([recordType, records]) => {
            // Handle different possible formats of records
            const recordsArray = Array.isArray(records) ? records : [records]

            return recordsArray.map((record, index) => {
              // Handle different possible record formats
              let name = ''
              let type = ''
              let value = ''

              if (typeof record === 'string') {
                // Simple string value
                value = record
              } else if (typeof record === 'object' && record !== null) {
                // Object with name/value or other properties
                name = record.name || record.host || ''
                type = record.type || recordType

                // Use rrData array for value if available
                if (Array.isArray(record.rrData)) {
                  // We'll handle the array rendering directly in the JSX
                  value = '' // This will be ignored as we'll render the array directly
                } else {
                  value = record.value || record.target || record.address || JSON.stringify(record)
                }
              }

              return (
                <div
                  key={`${type}-${index}`}
                  className="grid grid-cols-8 gap-2 py-1 border-b border-gray-100 dark:border-gray-800"
                >
                  <div className={`col-span-1 font-semibold ${getRecordTypeColor(type)}`}>
                    {type.toUpperCase()}
                  </div>
                  <div className="col-span-3 truncate" title={name}>
                    {name || '@'}
                  </div>
                  <div className="col-span-4 break-all">
                    {Array.isArray(record.rrData) ? (
                      <div className="flex flex-col">
                        {record.rrData.map((item: string, i: Key | null | undefined) => (
                          <div key={i} className="py-0.5 flex">
                            {record.rrData.length > 1 && (
                              <span className="mr-1 text-gray-500">➤</span>
                            )}
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      value
                    )}
                  </div>
                </div>
              )
            })
          })}
        </div>
      )
    }

    return (
      <div className="break-inside-avoid-column">
        <div className="bg-white shadow overflow-hidden rounded-lg dark:bg-gray-850 dark:highlight">
          <div className="py-4 border-b border-gray-200 px-4 sm:px-6 dark:border-gray-750/75">
            <div className="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap">
              <div className="ml-4 mt-2">
                <h3 className="text-md leading-6 font-medium text-gray-900 dark:text-gray-300">
                  DNS
                </h3>
              </div>
              <div className="ml-4 mt-2 flex-shrink-0">
                {!meetsMinimumVersion(nfd, '3') && <b>Requires V3+</b>}
              </div>
            </div>
          </div>
          <div className="ml-4 mr-2 mt-2">{renderDnsRecords()}</div>
        </div>
      </div>
      // <Card title="DNS">
      //   <div className="ml-4 mr-2 mt-2">{renderDnsRecords()}</div>
      // </Card>
    )
  } catch (error) {
    // If parsing fails, render as normal text with a warning
    console.warn(`Failed to parse 'dns' field as JSON: ${error}`)
    return null
  }
}
